import React, { useEffect, useState } from 'react';
import {
  IonRow,
  IonCol,
  IonPage,
  IonContent,
  IonLoading,
  IonBadge,
  IonFab,
  IonButton,
  IonIcon,
  IonFabButton
} from '@ionic/react';
import { connect } from 'react-redux';
import AppHeader from '../../components/AppHeader';
import { useHistory } from 'react-router';
import { AppointmentsRootState } from '../../store/reducers/appointments';
import './AppointmentsSelection.scss';
import { AuthenticationRootState } from '../../store/reducers/authentication';
import { CheckingStepProps } from '../../navigators/CheckinNavigator';
import { Appointment, Laboratory, Patient } from '../../models';
import PatientRepository from '../../repositories/PatientRepository';
import AppointmentCard from '../../components/AppointmentCard';
import { arrowForwardOutline } from 'ionicons/icons';
import StepperCheckin from '../../components/StepperCheckin';
import AppointmentRepository from '../../repositories/AppointmentRepository';
import EventBus from '../../services/EventBus';
import PatientSwitcher from '../../components/PatientSwitcher';

type AppointmentsSelectionProps = {
  laboratories: Laboratory[];
  newAppointmentRouterLink: string;
};

const mapStateToProps = (
  state: {
    authentication: AuthenticationRootState;
    appointments: AppointmentsRootState;
  },
  ownProps: any
) => {
  return {
    user: state.authentication.user!,
    sidep: state.appointments.sidep,
    isLoggedIn: !!state.authentication.accessToken,
    patientToUpdate:
      state.appointments.appointment.patient ||
      state.authentication.user?.patient
  };
};

const mapDispatchToProps = (dispatch: Function, ownProps: any) => ({});

const AppointmentsSelection: React.FC<
  ReturnType<typeof mapDispatchToProps> &
    AppointmentsSelectionProps &
    CheckingStepProps &
    ReturnType<typeof mapStateToProps>
> = ({
  user,
  step,
  nextStepRouterLink = '/',
  laboratories,
  newAppointmentRouterLink = '/'
}) => {
  const history = useHistory();

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [filteredAppointments, setFilteredAppointments] =
    useState(appointments);
  const [selectedAppointments, setSelectedAppointments] = useState<
    Appointment[]
  >([]);
  const [appointmentFetched, setAppointmentFetched] = useState(false);

  const [currentEditionPatient, setCurrentEditionPatient] = useState<Patient>(
    user.patient
  );

  async function fetchAppointments() {
    setIsLoading(true);

    if (laboratories.length) {
      const fetchedAppointments = (
        await PatientRepository.getAppointments()
      ).filter(
        (appointment) =>
          laboratories.some(
            (laboratory) => laboratory.id === appointment.laboratory?.id
          ) &&
          appointment.isToday &&
          !appointment.checked_at
      );
      setAppointments(fetchedAppointments);
      setAppointmentFetched(true);
    } else {
      history.push('/');
      EventBus.error(
        "Ce laboratoire semble rencontrer un problème de connexion. Veuillez vous signaler auprès du personnel d'accueil."
      );
    }

    setIsLoading(false);
  }

  function onSelectAppointment(appointment: Appointment) {
    const index = selectedAppointments.indexOf(appointment);

    if (index === -1) {
      setSelectedAppointments([...selectedAppointments, appointment]);
    } else {
      setSelectedAppointments(
        selectedAppointments.filter(
          (tempAppointment) => tempAppointment !== appointment
        )
      );
    }
  }

  function submitAppointment() {
    setIsSubmitLoading(true);

    AppointmentRepository.checkin(selectedAppointments)
      .then(() => {
        setIsSubmitLoading(false);

        if (nextStepRouterLink) {
          history.replace(nextStepRouterLink);
        }
      })
      .catch(() => {
        setIsSubmitLoading(false);
      });
  }

  useEffect(() => {
    setFilteredAppointments(
      appointments.filter(
        (appointment) => appointment.patient?.id === currentEditionPatient.id
      )
    );
  }, [appointments, currentEditionPatient]);

  function submit() {
    submitAppointment();
  }

  useEffect(() => {
    if (!appointmentFetched) {
      fetchAppointments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <IonPage>
      <IonLoading isOpen={isSubmitLoading} />
      <AppHeader />
      <StepperCheckin step={step} />
      <IonContent>
        <div className="ion-padding wrapper wrapper--large">
          <IonRow className="ion-align-items-center ion-justify-content-between ion-text-center">
            <IonCol>
              <h2>
                Sélectionner le ou les créneaux de prise en charge concernés par
                votre visite.
              </h2>
            </IonCol>
          </IonRow>
          <PatientSwitcher
            className="ion-margin-bottom ion-justify-content-center"
            defaultPatient={currentEditionPatient}
            onSelectPatient={(patient: Patient) =>
              setCurrentEditionPatient(patient)
            }
          />
          {!isLoading ? (
            <>
              {filteredAppointments.length ? (
                filteredAppointments.map((appointment) => (
                  <AppointmentCard
                    appointment={appointment}
                    key={appointment.id}
                    deletable={false}
                    selectable={true}
                    opaque={true}
                    onSelect={onSelectAppointment}
                    selected={selectedAppointments.includes(appointment)}
                  />
                ))
              ) : (
                <div className="ion-text-center ion-padding">
                  <IonBadge
                    color="warning"
                    className="ion-margin-bottom ion-text-wrap"
                  >
                    Vous n'avez pas de créneau de prise en charge à venir pour
                    ce patient.
                  </IonBadge>
                  {(laboratories.length === 1 &&
                    laboratories[0].appointment_type === 'direct_only') ||
                  laboratories[0].appointment_type === 'all' ? (
                    <div>
                      <IonButton routerLink={newAppointmentRouterLink}>
                        Réserver mon créneau de prise en charge
                      </IonButton>
                    </div>
                  ) : null}
                </div>
              )}
            </>
          ) : (
            <AppointmentCard />
          )}
        </div>
      </IonContent>
      <IonFab vertical="bottom" horizontal="end" slot="fixed">
        <IonButton
          type="submit"
          onClick={() => submit()}
          className="ion-custom-hide-md-down"
          disabled={!selectedAppointments.length}
        >
          <IonIcon icon={arrowForwardOutline} slot="start" /> Confirmer ma
          présence
        </IonButton>
        <IonFabButton
          type="submit"
          onClick={() => submit()}
          className="ion-hide-md-up"
          disabled={!selectedAppointments.length}
        >
          <IonIcon icon={arrowForwardOutline} />
        </IonFabButton>
      </IonFab>
    </IonPage>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AppointmentsSelection);
